import { Box } from '@mui/material'
import Processing from 'ext-ui/components/Processing/Processing'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAgentChat } from 'src/agent/components/AgentChat/AgentChat.queries'
import {
  ChatBox,
  ChatMarkdown,
  ChatWrapper,
  InputBox,
  LoadingText,
  MessageBox,
  MessageWrapper,
  Timestamp,
} from 'src/agent/components/AgentChat/AgentChat.style'
import AgentChatField from 'src/agent/components/AgentChatField/AgentChatField'
import { useLoopingTypingEffect } from 'src/hooks'
import { translateSingleString } from 'src/localization/Trns'
import InsightsTileRenderer from 'src/page-components/InsightsPage/InsightsTileRenderer/InsightsTileRenderer'
import {
  ConversationState,
  InterruptResolution,
  StructuredJsonType,
} from 'src/queries/agent/network/agent.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

export interface AgentChatProps {
  chatKey?: string
}

const AgentChat: React.FC<AgentChatProps> = ({ chatKey = 'default' }) => {
  const locale = useSelector(selectNullSafeLocaleFromState)

  const chatBoxRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef<HTMLDivElement>(null)
  const [userInput, setUserInput] = useState<string>('')

  const { isPending, messages, conversationState, startNewMessage } =
    useAgentChat(chatKey)

  const lastMessage = useMemo(() => {
    if (messages.length === 0) return undefined
    return messages[messages.length - 1]
  }, [messages])

  const userPromptPlaceholder = useMemo(() => {
    const isInterrupt = lastMessage?.type === ConversationState.INTERRUPT
    const naturalFeedbackOption = lastMessage?.options?.find(
      (option) =>
        option.action === InterruptResolution.NATURAL_LANGUAGE_FEEDBACK,
    )
    const acceptsNaturalLanguageFeedback = !!naturalFeedbackOption
    const hasSuggestedPrompts =
      !!lastMessage?.suggestedPrompts && lastMessage.suggestedPrompts.length > 0

    if (isInterrupt && acceptsNaturalLanguageFeedback) {
      return naturalFeedbackOption.label
    } else if (isInterrupt && !acceptsNaturalLanguageFeedback) {
      return translateSingleString(locale, 'Please select an action')
    } else if (hasSuggestedPrompts) {
      return translateSingleString(locale, 'Or enter your request')
    }

    return translateSingleString(locale, 'Enter your request')
  }, [lastMessage])

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM has updated before scrolling
    const scrollToBottom = () => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTo({
          behavior: 'smooth',
          top: chatBoxRef.current.scrollHeight,
        })
      }
    }

    // wait for content to be rendered
    const rafId = requestAnimationFrame(scrollToBottom)

    return () => cancelAnimationFrame(rafId)
  }, [messages, isPending])

  useEffect(() => {
    return () => {
      setUserInput('')
    }
  }, [])

  const loadingText = useLoopingTypingEffect([
    translateSingleString(locale, 'Loading...'),
    translateSingleString(locale, 'Working on it...'),
    translateSingleString(locale, 'Just a moment...'),
    translateSingleString(locale, 'Processing your request...'),
    translateSingleString(locale, 'Thanks for your patience...'),
  ])

  const sendUserInput = (prompt: string): void => {
    if (prompt === '') return
    setUserInput('')
    switch (conversationState) {
      case ConversationState.INTERRUPT:
        startNewMessage({
          currentState: ConversationState.INTERRUPT,
          resolution: InterruptResolution.NATURAL_LANGUAGE_FEEDBACK,
          feedback: prompt,
        })
        break
      case ConversationState.MESSAGE:
        startNewMessage({
          currentState: ConversationState.MESSAGE,
          prompt,
        })
        break
      default:
        return conversationState
    }
  }

  const sendInterruptResolution = (
    resolution: InterruptResolution,
    feedback: string,
  ): void => {
    if (conversationState !== ConversationState.INTERRUPT) {
      // Silently ignore invalid state - should not happen in normal flow
      return
    }
    setUserInput('')
    startNewMessage({
      currentState: ConversationState.INTERRUPT,
      resolution,
      feedback,
    })
  }

  return (
    <ChatWrapper>
      <ChatBox ref={chatBoxRef}>
        {messages.length === 0 && !isPending && (
          <MessageWrapper sender="ai">
            <MessageBox sender="ai">
              <ChatMarkdown>
                {translateSingleString(locale, 'How can I help you today?')}
              </ChatMarkdown>
            </MessageBox>
          </MessageWrapper>
        )}
        {messages.map((msg, index) => {
          const messageKey = `${chatKey}-${index}-${msg.timestamp}-${msg.sender}`
          return (
            <Box key={messageKey}>
              <MessageWrapper sender={msg.sender}>
                <Timestamp>{msg.timestamp}</Timestamp>
                <MessageBox sender={msg.sender}>
                  {msg.content && <ChatMarkdown>{msg.content}</ChatMarkdown>}
                  {msg.contentSrc && (
                    <ChatMarkdown>
                      {translateSingleString(locale, msg.contentSrc)}
                    </ChatMarkdown>
                  )}
                  {msg.structuredJson &&
                    msg.structuredJson.type ===
                      StructuredJsonType.TILE_DATA && (
                      <Box sx={{ marginTop: 2 }}>
                        <InsightsTileRenderer
                          tileSpec={msg.structuredJson.data}
                        />
                      </Box>
                    )}
                </MessageBox>
              </MessageWrapper>
            </Box>
          )
        })}
        {isPending && (
          <LoadingText ref={loadingRef}>
            <Processing /> {loadingText}
          </LoadingText>
        )}
      </ChatBox>
      <InputBox>
        <AgentChatField
          value={userInput}
          options={messages[messages.length - 1]?.options}
          onOptionClick={(action, prompt): void =>
            sendInterruptResolution(action, prompt)
          }
          suggestedPrompts={messages[messages.length - 1]?.suggestedPrompts}
          onSuggestedPromptClick={(promptTemplate): void => {
            startNewMessage({
              currentState: ConversationState.MESSAGE,
              promptTemplate,
            })
          }}
          onChange={(value): void => setUserInput(value)}
          onSubmit={(): void => sendUserInput(userInput)}
          placeholder={userPromptPlaceholder}
          disabled={isPending}
          loading={isPending}
        />
      </InputBox>
    </ChatWrapper>
  )
}

export default AgentChat
